import { isEmpty } from '@fruitful/fruitils';
import { RequiredGuideMediaInstructions } from '@fruitful/guidance';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useForm } from 'react-hook-form';
import {
    BodyLargeEmphasized,
    Button,
    ButtonVariant,
    ControlledInput,
    ControlledMultilineInput,
    ControlledPicker,
    Form,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { allGuidesAtom } from '../../entities/entityAtoms';
import { GuideStatus } from '../../entities/entityTypes';
import type { CreateGuideDto } from '../types/GuideTypes';

const createGuideFormSchema = z.object({
    email: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    signUpId: z.string().nullish(),
    profileImageUrl: z.string().nullish(),
    pronouns: z.string().nullish(),
    status: z.nativeEnum(GuideStatus),
    slackId: z.string().nullish(),
    firefliesApiKey: z.string().nullish(),
    bio: z.string().nullish(),
    styles: z.string().nullish(),
    advisorId: z.string().nullish(),
});

type GuideFormSchema = z.infer<typeof createGuideFormSchema>;

interface GuideFormProps {
    onSubmit: (data: CreateGuideDto) => Promise<void>;
    onCancel: () => void;
    isSubmitting: boolean;
}

const GUIDE_STATUS_OPTIONS = [
    { label: 'Active', value: GuideStatus.ACTIVE },
    { label: 'Inactive', value: GuideStatus.INACTIVE },
    { label: 'Left Company', value: GuideStatus.LEFT_COMPANY },
];

export const CreateGuideForm = ({
    onSubmit,
    onCancel,
    isSubmitting,
}: GuideFormProps) => {
    const toast = useToastController();
    const advisors = useAtomValue(allGuidesAtom);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        control,
        watch,
    } = useForm<GuideFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(createGuideFormSchema),
        defaultValues: {
            email: '',
            firstName: '',
            lastName: '',
            signUpId: '',
            profileImageUrl: '',
            pronouns: '',
            status: GuideStatus.INACTIVE,
            slackId: '',
            firefliesApiKey: '',
            bio: '',
            styles: '',
            advisorId: undefined,
        },
    });

    const onFormSubmit = async (data: GuideFormSchema) => {
        try {
            const submitData: CreateGuideDto = {
                email: data.email.trim(),
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                signUpId: !isEmpty(data.signUpId?.trim())
                    ? data.signUpId?.trim()
                    : undefined,
                profileImageUrl: !isEmpty(data.profileImageUrl?.trim())
                    ? data.profileImageUrl?.trim()
                    : undefined,
                pronouns: !isEmpty(data.pronouns?.trim())
                    ? data.pronouns?.trim()
                    : undefined,
                status: data.status,
                slackId: !isEmpty(data.slackId?.trim())
                    ? data.slackId?.trim()
                    : undefined,
                firefliesApiKey: !isEmpty(data.firefliesApiKey?.trim())
                    ? data.firefliesApiKey?.trim()
                    : undefined,
                bio: !isEmpty(data.bio?.trim()) ? data.bio?.trim() : undefined,
                styles: !isEmpty(data.styles?.trim())
                    ? data.styles?.trim()
                    : undefined,
                advisorId:
                    data.advisorId != null && !isEmpty(data.advisorId.trim())
                        ? data.advisorId.trim()
                        : undefined,
            };

            await onSubmit(submitData);
            toast.show(
                'Guide saved successfully!',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            toast.show('Error saving guide', MutationErrorToastOptions(error));
        }
    };

    return (
        <YStack gap={16} padding={16}>
            <BodyLargeEmphasized>Guide Information</BodyLargeEmphasized>

            <Form onSubmit={handleSubmit(onFormSubmit)}>
                <YStack gap={16}>
                    <ControlledInput
                        name="firstName"
                        control={control}
                        register={register}
                        errors={errors}
                        label="First Name"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="lastName"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Last Name"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="email"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Email"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="pronouns"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Pronouns"
                        disabled={isSubmitting}
                    />

                    <ControlledPicker
                        name="status"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Status"
                        options={GUIDE_STATUS_OPTIONS}
                        disabled={isSubmitting}
                    />

                    <ControlledPicker
                        name="advisorId"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Advisor"
                        placeholder="Select an advisor (optional)"
                        options={advisors.map((advisor) => ({
                            label: advisor.fullName,
                            value: advisor.id,
                        }))}
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="signUpId"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Sign Up ID"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="profileImageUrl"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Profile Image URL"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="slackId"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Slack ID"
                        disabled={isSubmitting}
                    />

                    <ControlledInput
                        name="firefliesApiKey"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Fireflies API Key"
                        disabled={isSubmitting}
                    />

                    <ControlledMultilineInput
                        name="bio"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Bio"
                        numberOfLines={4}
                        disabled={isSubmitting}
                    />

                    <ControlledMultilineInput
                        name="styles"
                        control={control}
                        register={register}
                        errors={errors}
                        label="Styles"
                        numberOfLines={3}
                        disabled={isSubmitting}
                    />
                </YStack>

                <YStack paddingVertical={24}>
                    <RequiredGuideMediaInstructions
                        guideSignupId={watch('signUpId')}
                    />
                </YStack>

                <YStack gap={12} justifyContent="flex-end" marginTop={16}>
                    <Button
                        text="Create Guide"
                        variant={ButtonVariant.PRIMARY}
                        onPress={handleSubmit(onFormSubmit)}
                        disabled={isSubmitting || !isValid}
                    />
                    <Button
                        text="Cancel"
                        variant={ButtonVariant.SECONDARY}
                        onPress={onCancel}
                        disabled={isSubmitting}
                    />
                </YStack>
            </Form>
        </YStack>
    );
};
